/**
 * The work-order record — `/countries/:cc/work/:workOrderId`, Figma page 04.
 *
 * The board links here (`WorkBoardPage`, card `<Link to=…/work/:id>`), and until
 * this file existed that link was mounted-but-dead. Everything a work order can
 * have done TO it happens here, because the board deliberately does not drag:
 * every move is the consequence of recording a fact, not of a gesture.
 *
 * ===========================================================================
 * THE MONEY CONTRAST, WHICH IS THE POINT OF THE CRF CARD (brief-mandatory)
 * ===========================================================================
 * The card shows `crf.amount` EXACTLY AS IT ARRIVED. A Coordinator or Country
 * Manager reading this page sees the figure; a Developer sees `'•••'`, because
 * the amounts interceptor substituted it server-side against `crf.country_id` —
 * the nested node's own country. This screen holds no permission logic for money
 * at all, and that is deliberate: a UI that decided when to mask would be a
 * second implementation of a rule that must have exactly one. What it must not do
 * is PARSE that string. `Number('•••')` is `NaN`, and a formatter applied to the
 * masked value would put "NaN" where a masked amount belongs — which reads as a
 * bug and invites somebody to "fix" it by unmasking.
 *
 * `amounts.view` is therefore never consulted here. The contrast is measured in
 * `work-orders.e2e-spec.ts` at the route, and in this feature's vitest at the
 * screen — both directions, because one side proves nothing.
 *
 * ===========================================================================
 * ESC-3c — NO VERIFIED NODE BACKS THE ASSIGN POPOVER OR THIS CARD
 * ===========================================================================
 * Owner-ruled: build them to the API contract in house style, and say so. The DL
 * dropzone (12:5281) and file table (12:5586) ARE transcribed from verified nodes;
 * the CRF card and the assign popover are not, and no comment in this feature
 * claims a measurement for them. The house style is itself a verified system, so
 * they inherit rather than invent — but "inherited" is not "measured", and the
 * report says which is which.
 *
 * ===========================================================================
 * WHO IS ON THE CARD (OQ-WO-ASSIGNEE-NAME, owner-ruled)
 * ===========================================================================
 * `assigned_to` is a user id, and this screen used to render `#42` because
 * `GET /users` is behind `users.manage` and a Developer does not hold it. The
 * owner ruled that `crf.view` implies reading a colleague's DISPLAY NAME — "a
 * board that can't say who's on a card isn't a board" — so `GET /users/:id/card`
 * returns the name and an avatar flag, and NOTHING else. The full record stays
 * behind `users.manage`, and `user-card.e2e-spec.ts` proves that with the same
 * token that reads the card.
 */

import { Link, useParams } from 'react-router-dom';

import { useCountryCode } from '../../auth/guards';
import { useSession } from '../../auth/session';
import { Alert, CountryBadge } from '../../components/ui';
import { useToast } from '../inquiries/toast';
import { UserAvatar } from '../avatars/UserAvatar';
import { useUserCard } from '../directory/use-directory';
import { AssignPopover } from './AssignPopover';
import { PaymentPanel, TestingPanel } from './LifecyclePanels';
import { LifecycleSummary } from './SignoffPanel';
import { WorkFilesPanel } from './WorkFilesPanel';
import { useAssignWork, useWorkOrderRecord } from './use-work';
import { workErrorText } from './errors';
import type { WorkOrderRecordDto } from './api';

const WORK_TYPE_LABEL: Readonly<Record<string, string>> = {
  new: 'New',
  correction: 'Correction',
  dl_file: 'DL file',
  ready_deploy: 'Ready to deploy',
};

/** Underscored API statuses read as words. No mapping table: nothing is renamed. */
function humanise(value: string): string {
  return value.replace(/_/g, ' ');
}

export function WorkOrderRecordPage(): React.JSX.Element {
  const countryCode = useCountryCode();
  const { workOrderId } = useParams<{ workOrderId: string }>();
  const { can, me, isGroupAdmin } = useSession();
  const toast = useToast();

  const canView = can(countryCode, 'crf.view');
  const id = workOrderId ?? null;
  const record = useWorkOrderRecord(id, canView);
  const assign = useAssignWork(countryCode);
  // Resolved from `assigned_to`, which arrives with the record. Null id → the
  // hook stays disabled, so an unassigned work order costs no request.
  const assignee = useUserCard(record.data?.assigned_to ?? null);

  if (!canView) {
    return (
      <div className="p-6">
        <Alert tone="danger" title="This work order is not available to this account">
          Opening development work in {countryCode} needs the <code>crf.view</code>{' '}
          permission.
        </Alert>
      </div>
    );
  }

  if (record.isPending) {
    return (
      <div className="flex flex-col gap-4 p-6">
        <div className="h-8 w-64 animate-pulse rounded-sm bg-subtle" />
        <div className="h-40 animate-pulse rounded-md bg-subtle" />
      </div>
    );
  }

  if (record.isError || record.data === undefined) {
    return (
      <div className="flex flex-col gap-4 p-6">
        <Alert tone="danger" title="This work order could not be opened">
          {workErrorText(record.error)}
        </Alert>
        <Link
          to={`/countries/${countryCode}/work`}
          className="w-fit text-body13 text-fg underline underline-offset-2 hover:decoration-2"
        >
          Back to the work board
        </Link>
      </div>
    );
  }

  const work: WorkOrderRecordDto = record.data;
  const myUserId = me === null ? null : String(me.id);
  const assignedToMe = work.assigned_to !== null && work.assigned_to === myUserId;

  return (
    <div className="flex flex-col gap-4 p-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <Link
            to={`/countries/${countryCode}/work`}
            className="text-label text-fg-muted underline underline-offset-2 hover:text-fg"
          >
            ← Work board
          </Link>
          <h1 className="mt-1 flex flex-wrap items-center gap-2 text-h2 font-semibold text-fg">
            {work.crf_doc_no ?? `Work order ${work.id}`}
            <CountryBadge code={countryCode} />
          </h1>
          <p className="text-body13 text-fg-muted">
            {WORK_TYPE_LABEL[work.type] ?? work.type} · {humanise(work.status)}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <p
            className="flex items-center gap-2 text-body13 text-fg-secondary"
            data-testid="work-assignee"
          >
            {work.assigned_to === null ? (
              'Unassigned'
            ) : assignedToMe ? (
              'Assigned to you'
            ) : (
              <>
                {/* The face and the name, both readable by any `crf.view` holder
                  * (the avatar always was; the name since OQ-WO-ASSIGNEE-NAME was
                  * ruled). While the card is in flight the id is shown rather than
                  * a blank — a name that pops in later is better than a gap that
                  * looks like a bug, and worse than neither would be inventing a
                  * placeholder name. */}
                <UserAvatar
                  name={assignee.data?.name ?? ''}
                  userId={work.assigned_to}
                  size={24}
                  decorative
                />
                <span>
                  Assigned to {assignee.data?.name ?? `#${work.assigned_to}`}
                </span>
              </>
            )}
          </p>
          {/* D5: no `work.assign` → no control. A Developer reads this page and
            * cannot reassign their own job, which is the intended authority. */}
          {can(countryCode, 'work.assign') ? (
            <AssignPopover
              countryCode={countryCode}
              myUserId={myUserId}
              canReadDirectory={isGroupAdmin || can(countryCode, 'users.manage')}
              busy={assign.isPending}
              // v22-E2: a patch job with no serial yet asks for it up front —
              // the API refuses without it (ruling 4), and a picker that lets
              // the operator choose and THEN 422s asks at the wrong moment.
              requireSerial={work.is_gov_patch && work.tally_serial === null}
              onAssign={(userId, name, tallySerial) => {
                assign.mutate(
                  { workOrderId: work.id, userId, tallySerial },
                  {
                    onSuccess: (result) => {
                      // RULE 11 — `notified:false` means the assignment stuck and
                      // NOBODY WAS TOLD. The API puts it on the wire rather than
                      // swallowing it; swallowing it HERE would be the same defect
                      // one layer up.
                      toast.push({
                        text: result.notified
                          ? `Assigned to ${name ?? 'you'}.`
                          : `Assigned to ${name ?? 'you'}, but the notification did not go out. Tell them directly.`,
                        tone: result.notified ? 'success' : 'error',
                      });
                    },
                    onError: (error) =>
                      toast.push({ text: workErrorText(error), tone: 'error' }),
                  },
                );
              }}
            />
          ) : null}
        </div>
      </header>

      <CrfCard work={work} />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <WorkFilesPanel
          workOrderId={work.id}
          canView={canView}
          canUpload={can(countryCode, 'work.files.manage')}
        />
        <TestingPanel
          record={work}
          countryCode={countryCode}
          canRecord={can(countryCode, 'testing.record')}
          onToast={(text, tone) => toast.push({ text, tone })}
        />
        <LifecycleSummary
          work={work}
          countryCode={countryCode}
          canDeploy={can(countryCode, 'deploy.record')}
          canSignDeveloper={can(countryCode, 'signoff.developer')}
          canSignProject={can(countryCode, 'signoff.project')}
          onToast={(text, tone) => toast.push({ text, tone })}
        />
        <PaymentPanel
          crfId={work.crf.id}
          current={work.crf.payment_status}
          countryCode={countryCode}
          canRecord={can(countryCode, 'billing.record')}
          onToast={(text, tone) => toast.push({ text, tone })}
        />
      </div>
    </div>
  );
}

/**
 * The CRF this work hangs off — Figma 12:1848 by intent, HOUSE STYLE by
 * construction (ESC-3c).
 *
 * The amount is rendered verbatim. See the file header for why nothing here
 * parses, formats or arithmetises it.
 */
function CrfCard({ work }: { work: WorkOrderRecordDto }): React.JSX.Element {
  const { crf } = work;
  return (
    <section
      aria-labelledby="crf-card-heading"
      className="rounded-md border border-line bg-surface p-4"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 id="crf-card-heading" className="text-body font-medium text-fg">
            {crf.title}
          </h2>
          <p className="tnum text-label text-fg-muted">
            {crf.doc_no} · {humanise(crf.status)}
          </p>
        </div>
        {/* NO LINK TO THE CRF. `domain/nav.ts` reserves `crfs` with
          * `available: false` and no route is mounted — the CRF's own screen is
          * Phase 3's and is not built. A link styled as one that 404s is worse
          * than the sentence below it, which is true. */}
        <p className="text-label text-fg-muted">
          The CRF&rsquo;s own screen is not built yet.
        </p>
      </div>

      <dl className="mt-3 flex flex-wrap gap-x-8 gap-y-2">
        <div>
          <dt className="text-label text-fg-muted">Value</dt>
          {/* VERBATIM. `'•••'` for a caller without `amounts.view` in this CRF's
            * country, the figure for one who holds it — decided server-side, and
            * this element cannot tell which it received. */}
          <dd className="tnum text-body font-medium text-fg" data-testid="crf-amount">
            {crf.amount === null ? '—' : crf.amount}
            {crf.currency === null ? '' : ` ${crf.currency}`}
          </dd>
        </div>
        <div>
          <dt className="text-label text-fg-muted">Payment</dt>
          <dd className="text-body text-fg">
            {crf.payment_status === null ? 'Not set' : humanise(crf.payment_status)}
          </dd>
        </div>
      </dl>
    </section>
  );
}
